import { RenderOptions } from 'nordic/layout';
import type { Request } from 'express';
import type { PlatformId } from 'frontend-platform_detection';
import type { MelidataTrack } from './Track';
import { validContextuals } from '../app/server/middlewares/valid-contextual';

/* eslint-disable camelcase */
export type HubStatus = 'in_progress' | 'finished' | 'under_age_finished';

export type StepStatus = 'pending' | 'completed' | 'conflict' | 'blocked';

export type Step = 'email_validation' | 'declared_email' | 'phone_validation' | 'kyc' | 'pix' | 'regulations' |
  'password' | 'kyc_company' | 'tyc' | 'checked_email_validation' | 'kyc_light' | 'checked_invitation_email_validation' | 'kyc_unified' | 'kyc_on_the_fly' | 'kyc_business_data' | 'kyc_legal_representative' | 'kyc_with_session' | 'tyc_with_session' | 'phone_validation_with_session';

export type View = 'landing' | 'splitter' | 'hub' | 'congrats' | 'congrats_pending' |'step_congrats' | 'resume'| 'recovery' | 'error' | 'privacy' | 'use_policies' | 'max_attempts' | 'feedback_step' | 'conditions_blocker' | 'fiscal_splitter' | 'remunerated_account' | 'corporate_name' | 'rfc_splitter' | 'challenge_complete' | 'cross_resume';

export type Ifpe = 'wallet' | 'standard';

export type Contextual = typeof validContextuals[number];

export type Entity = 'pf' | 'pj' | 'no_apply'

export type AbuseScore = 'low' | 'high' | 'very_high'

export type Business = 'FINTECH' | 'MARKETPLACE' | 'NONE'

export type ContextualData = {
  id: string;
  parameters: {
    invitation_id?: string;
    invitation_flow?: string;
  }
}

type Link = {
  last: {
    uri: string;
  },
  next: {
    uri: string;
  }
}

export type GeneralStepInfo = {
  status: StepStatus;
  contextual: Contextual;
  entity: Entity;
  redirect_url: string;
  uri?: string;
  abuse_scoring?: AbuseScore | undefined;
  contextual_data?: ContextualData;
  entity_transitioned?: boolean;
  extra_info?: ExtraInfo;
}

export type StepInfo = GeneralStepInfo & {
  id: Step;
  data?: any;
};

export type SocialStepInfo = GeneralStepInfo & {
  id: 'marketplace_social';
  data?: {
    social_data: string;
    social_data_source: string;
  }
};

export type Cookie = {
  name: string;
  value: string;
  domain: string;
  path: string;
  secure: boolean;
  http_only: boolean;
  max_age: number;
  same_site?: string;
  main_value?: string;
};

export interface HubStepsInfo {
  registration_id: string;
  status: HubStatus;
  steps: Array<StepInfo>;
  links: Pick<Link, 'last'>;
  contextual: Contextual;
  entity: Entity;
  abuse_scoring?: AbuseScore | undefined;
  contextual_data?: ContextualData;
  entity_transitioned?: boolean;
  extra_info?: ExtraInfo
  is_resumed?: boolean;
}

export interface ExtraInfo {
  experiments?: ExperimentsData;
  isNewFeedbackEnabled?: boolean;
  user_internal_tags?: Array<string>;
  isNewSellersHub?: boolean;
}

export interface HubInfoInit extends HubStepsInfo {
  cookies: Array<Cookie>;
}

export type StepCompleteInfo = {
  links: Pick<Link, 'next'>;
};

export type ImporterCookies = {
  ftid: string;
  ssid: string;
};

// TODO: separate MP and ML keys once the migration is done
export type ImporterKeys = Partial<{
  ssid: {
    value: string;
  };
  ftid: {
    value: string;
  };
  nonce_sso: {
    value: string;
  };
  mp_wsid: {
    value: string;
  };
}>;

export type AccessToken = {
  name: 'ssid' | 'ftid' | 'nonce_sso' | 'mp_wsid',
  value: string,
}

export type NonceToken = {
  name: 'nonce_sso',
  value: string,
}

export type WebSession = {
  ftid: string;
  web_session: {
    access_tokens: Array<AccessToken>;
    scopes: Array<string>;
    nonce_tokens: Array<NonceToken>;
  };
};

// TODO: remove legacy session once the migration is done
export type LegacySession = {
  ssid: string;
  ftid: string;
};

export type Session = WebSession | WebSession & LegacySession | LegacySession;

export type RegistrationCompleteInfo = {
  session?: Session;
  links: Pick<Link, 'last'>;
};

export type ImporterResponse = {
  cookies: Array<Cookie>;
  _links: {
    replication: {
      href: string
    }
  };
}

export type PreStepsResponse = {
  user_email: string;
  stored_user_id?: string;
  login_redirect: boolean;
};

export type ResumeResponse = {
  registration_id: string
  cookies: Array<Cookie>
  links: {
    first: {
      uri: string
    }
    last: {
      uri: string
    }
  }
}

export type Links = {
  links: {
    first: {
      uri: string
    }
  }
}

export type PiiDataName = {
  names: {
    preferred: string;
    preferred_full: string;
  },
  person?: {
    first_name: string,
    last_name: string
  }
}

export type PutDeclaredEmail = {
  id: Step,
  status: string,
  uri: string
}

export type RedirectResponse = {
  redirect_url: string;
};

export type NativeOs = 'Android' | 'iOS';

export enum PoliciesActionables {
  DDP = 'DDP',
  TDP = 'TDP',
  DDP_MP = 'DDP_MP',
  DDP_ML = 'DDP_ML',
  TYC = 'TYC',
  TYC_MP = 'TYC_MP',
  TYC_ML = 'TYC_ML',
}

export enum Policies {
  TYC = 'tyc',
  DDP = 'ddp',
}

export enum PlatformsId {
  MP = 'MP',
  ML = 'ML',
  TC = 'TC',
  PI = 'PI',
  MS = 'MS',
  MT = 'MT',
  GI = 'GI',
  TI = 'TI',
  TM = 'TM',
  TL = 'TL',
  AP = 'AP',
  BC = 'BC',
  DR = 'DR',
  BO = 'BO',
}

export enum SitesId {
  CBT = 'CBT',
  MCO = 'MCO',
  MCR = 'MCR',
  MEC = 'MEC',
  MLA = 'MLA',
  MLB = 'MLB',
  MLC = 'MLC',
  MLM = 'MLM',
  MLU = 'MLU',
  MLV = 'MLV',
  MPA = 'MPA',
  MPE = 'MPE',
  MPT = 'MPT',
  MRD = 'MRD',
  MBO = 'MBO',
  MPY = 'MPY',
  MGT = 'MGT',
  MHN = 'MHN',
  MNI = 'MNI',
  MSV = 'MSV',
  MCU = 'MCU',
  ALL = 'ALL',
}

export enum StatusCode {
  BAD_REQUEST = 400,
  NOT_FOUND = 404,
  LOCKDOWN = 451,
  INTERNAL_SERVER_ERROR = 500
}

export type StepCongratsContent = {
  title: string
  description?: string
}

export type StepContent = {
  title: string
  description: string
  action?: string
}

export type StepsWording = Record<Step, StepContent>

export type SplitterWordingProps = {
  title: string
  personal_button: {
    title: string
    subtitle: string
    cta: string
  }
  company_button: {
    title: string
    subtitle: string
    cta: string
  }
}

export type PoliciesWordings = {
  title: string
  caption: string
  actionables: {
    [key in PoliciesActionables]?: string
  },
  errorWording: string
}

export type BlockerReason = 'under_age_not_enabled' | 'unsupported_version'

type CrossResumeResponse = {
  is_cross_resume: boolean;
  links?: {
    first: {
      uri: string;
    }
  };
  cross_resume_flow: string;
}

export type FiscalSplitterData = {
  entity: string;
  fiscal_document_id?: string;
  is_company_name_enabled?: boolean;
  redirect_to_pro_account?: boolean;
  cross_resume?: CrossResumeResponse;
  creditsData?: Partial<Record<Experiments, string>>;
}

export type CorporateNameData = {
  corporateName: string;
}

export type RegulatedModalContent = 'MLM_WALLET' | 'DEFAULT'

export type RegulatedStepData = undefined | {
  platformId: string
  siteId: string
  stepId: string
  content: RegulatedModalContent
  uri: string
};

export type RegulatedStepValidation = {
  stepId: string
  uri: string
  isMobile: boolean
};

export type PercentageByDevice = {
  desktop: number;
  Android: number;
  iOS: number;
};

export type DeviceType = NativeOs | 'desktop';

export enum Profiles {
  b2b_ab_switch = 'b2b_ab_switch',
}

export enum Experiments {
  FORM_OVER_AGNOSTIC_EXPERIMENT = 'registration_form_over_agnostic',
  REGISTRATION_SOCIAL_EXPERIMENT = 'registration_social',
  STEPS_REORDER_PF_EXPERIMENT = 'registration_steps_reorder_pf_experiment',
  STEPS_REORDER_PJ_EXPERIMENT = 'registration_steps_reorder_pj_experiment',
  RECOVERY_EXPERIMENT = 'registration_recovery_experiment',
  CREDITS_EXPERIMENT = 'registration_credits_experiment',
  OTF_MLM_EXPERIMENT = 'otf_mlm',
  AGNOSTIC_OVER_AURORA_EXPERIMENT = 'registration_agnostic_over_aurora',
  REGISTRATION_B2B = 'registration_b2b',
}

export enum ExperimentsKeys {
  B2B_REGISTRATION = 'show',
}

export const ExperimentVariationIDs = {
  [Experiments.REGISTRATION_B2B]: {
    true: '103444',
    false: '103443',
  },
};

export enum ContextualType {
  Swap = 'swap',
  Normal = 'normal',
  Whatsapp = 'whatsapp',
  Sell = 'sell',
  Company = 'company',
  Buy = 'buy',
  Mgm = 'mgm',
  Question = 'question',
  Cart = 'cart',
  MshopsHighRisk = 'mshops_high_risk',
  Favorite = 'favorite',
  Valeras = 'valeras',
  Operator = 'operator',
  Vis = 'vis',
  RelationshipReferral = 'relationship_referral',
  Logistics = 'logistics',
  Flex = 'flex',
  ValerasEmployee = 'valeras_employee',
  UnifiedNormal = 'unified_normal',
  CrossBorderTrade = 'cross_border_trade',
  CrossBorderTradeOff = 'cross_border_trade_off',
  Crowd = 'crowd',
  Mplay = 'mplay',
  MarketplaceForm = 'marketplace_form',
  MarketplaceSocial = 'marketplace_social'
}

export enum StepType {
  EmailValidation = 'email_validation',
  DeclaredEmail = 'declared_email',
  PhoneValidation = 'phone_validation',
  KYC = 'kyc',
  PIX = 'pix',
  Regulations = 'regulations',
  Password = 'password',
  KYCCompany = 'kyc_company',
  TyC = 'tyc',
  CheckedEmailValidation = 'checked_email_validation',
  KYCLight = 'kyc_light',
  CheckedInvitationEmailValidation = 'checked_invitation_email_validation',
  KYCUnified = 'kyc_unified',
  KYCOnTheFly = 'kyc_on_the_fly',
  KYCBusinessData = 'kyc_business_data',
  KYCLegalRepresentative = 'kyc_legal_representative',
  KYCWithSession = 'kyc_with_session',
  TyCWithSession = 'tyc_with_session',
  PhoneValidationWithSession = 'phone_validation_with_session',
}

export enum RegistrationMethod {
  Parity = 'parity',
  Registration = 'registration'
}

export enum CookieNames {
  ParityV3 = 'parityv3',
  HckV3 = 'hckV3',
}

export enum CookiesMaxAge {
  ParityV3 = 86400000,
  HckV3 = 86400000,
}

export type ExperimentsData = Partial<
  (Record<
    Exclude<Experiments, Experiments.REGISTRATION_B2B>,
    string
  > & {
    [Experiments.REGISTRATION_B2B]?: boolean;
  })
>;


export type DefaultPageSettings = {
  settings: {
    title: string;
    melidata?: MelidataTrack
    renderParams: RenderOptions
  };
};

export enum BarLeftButtonStyle {
  BACK = 'back',
  CLOSE = 'close',
  MENU = 'menu',
  NONE = 'none'
}

export type CrossResumeLinks = {
  first: {
    uri: string
  }
}
export type PostCrossResumeResponse = {
  registration_id: string;
  cookies: Cookie[];
  links: CrossResumeLinks;
}

export enum ClientType {
  WEB = 'web',
  MOBILE = 'mobile',
}
type PlatformKeyForWordings = PlatformId | 'default';

type PlatformDescriptions = {
  [entity in Entity]?: string;
};

type SitePlatformDescriptions = {
  [platform in PlatformKeyForWordings]?: PlatformDescriptions;
} & {
  default?: string;
};

export type TextMapForWordings = {
  [site in SitesId]?: SitePlatformDescriptions;
} & {
  default: string;
};

export type PhoneDescriptionMapForWordings = {
  [site in SitesId]?: {
    [platform in PlatformKeyForWordings]?: PlatformDescriptions;
  };
} & {
  default: {
    [platform in PlatformKeyForWordings]?: PlatformDescriptions;
  } & {
    default: string;
  };
};

export type PlatformAvailabilityConfig = {
  web: boolean;
}

export type SiteIdType = Required<Request>['platform']['siteId']

export type B2BABSwitchConfigType = {
  [K in SiteIdType as Lowercase<string & K>]?: PlatformAvailabilityConfig
}

export enum HubStepsStatus {
  COMPLETED = 'completed',
  PENDING = 'pending',
  CONFLICT = 'conflict',
  BLOCKED = 'blocked',
}

export enum ResumeActionType {
  CONTINUE = 'continue',
  NEW_ACCOUNT = 'new_account'
}
